import { conns } from '@cfg/axiosConfig.tsx';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';

export interface ApiErrorPayload {
  detail?: unknown;
  message?: string;
}

export type ExpenseType = 'eventual' | 'periodic' | 'fixed' | 'variable' | 'indexed';
export type ExpenseFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type ExpenseStatus = 'REGISTERED' | 'SCHEDULED' | 'EXECUTED' | 'CANCELED';
export type ExpenseExecutionStatus =
  | 'REGISTERED'
  | 'SCHEDULED'
  | 'EXECUTED'
  | 'CANCELED'
  | 'pending' // ← legacy UI mapping until the calendar is updated
  | 'skipped';

export interface ExpenseOut {
  id: number;
  name: string;
  description: string | null;
  type: ExpenseType;
  status: ExpenseStatus;
  amount: string;
  amount_estimated: string | null;
  amount_confirmed: string | null;
  currency_code: string;
  cost_center_code: string | null;
  cost_center_name: string | null;
  due_date: string | null;
  schedule_frequency: ExpenseFrequency | null;
  schedule_interval: number | null;
  start_at: string;
  end_at: string | null;
  active_from: string | null;
  active_until: string | null;
  next_run_at: string | null;
  last_run_at: string | null;
  index_name: string | null;
  index_rate: string | null;
  is_active: boolean;
  is_purchase_order: boolean;
  auto_execute_on_due_date: boolean;
  default_cash_box_id: number | null;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by: number;
}

export interface ExpenseExecutionOut {
  id: number;
  expense_id: number;
  status: ExpenseExecutionStatus;
  scheduled_for: string;
  amount: string;
  currency_code: string;
  index_rate_applied: string | null;
  payment_method: string | null;
  installment_number: number | null;
  installments_total: number | null;
  created_at: string;
  created_by: number;
  executed_at: string | null;
  executed_by: number | null;
  notes: string | null;
  expense_name?: string | null;
  expense_type?: ExpenseType | null;
  expense_cost_center_code?: string | null;
}

export interface ExpenseDetailOut extends ExpenseOut {
  executions: ExpenseExecutionOut[];
}

export interface ExpenseCreatePayload {
  name: string;
  description?: string | null;
  type: ExpenseType;
  amount_estimated: number;
  amount_confirmed?: number | null;
  currency_code: string;
  cost_center_code?: string | null;
  due_date?: string | null;
  schedule_frequency?: ExpenseFrequency | null;
  schedule_interval?: number | null;
  start_at?: string | null;
  end_at?: string | null;
  active_from?: string | null;
  active_until?: string | null;
  index_name?: string | null;
  index_rate?: number | null;
  is_purchase_order?: boolean;
  auto_execute_on_due_date?: boolean;
  default_cash_box_id?: number | null;
}

export interface ExpenseUpdatePayload {
  name?: string;
  description?: string | null;
  type?: ExpenseType;
  amount_estimated?: number;
  amount_confirmed?: number | null;
  currency_code?: string;
  cost_center_code?: string | null;
  due_date?: string | null;
  schedule_frequency?: ExpenseFrequency | null;
  schedule_interval?: number | null;
  start_at?: string;
  end_at?: string | null;
  active_from?: string | null;
  active_until?: string | null;
  index_name?: string | null;
  index_rate?: number | null;
  auto_execute_on_due_date?: boolean;
  default_cash_box_id?: number | null;
  is_purchase_order?: boolean;
  is_active?: boolean;
}

export interface ExpenseExecutePayload {
  amount?: number | null;
  currency_code?: string;
  payment_method?: string | null;
  notes?: string | null;
  installments?: number | null;
}

export interface ExpenseSchedulePayload extends ExpenseExecutePayload {
  scheduled_for: string;
}

export interface ExpenseRematerializePayload {
  start_from?: string | null;
}

type QueryKey = readonly unknown[];

type ExpensesQueryResult<TData = ExpenseOut[]> = UseQueryResult<
  TData,
  AxiosError<ApiErrorPayload>
>;

type ExpenseDetailQueryResult<TData = ExpenseDetailOut> = UseQueryResult<
  TData,
  AxiosError<ApiErrorPayload>
>;

type ExpenseExecutionsQueryResult<TData = ExpenseExecutionOut[]> = UseQueryResult<
  TData,
  AxiosError<ApiErrorPayload>
>;

const EXPENSES_URL_KEYS = {
  list: 'erp:expenses:base' as const,
  detail: 'erp:expenses:detail' as const,
  executions: 'erp:expenses:executions' as const,
  allExecutions: 'erp:expenses:executions' as const,
  execute: 'erp:expenses:execute' as const,
  schedule: 'erp:expenses:schedule' as const,
  rematerialize: 'erp:expenses:rematerialize' as const,
} as const;

export const EXPENSES_QUERY_KEYS = {
  all: ['erp', 'expenses'] as const,
  list: (params: FetchExpensesParams) =>
    ['erp', 'expenses', 'list', params] as const,
  detail: (expenseId: number) => ['erp', 'expenses', 'detail', expenseId] as const,
  executions: (expenseId: number) =>
    ['erp', 'expenses', 'executions', expenseId] as const,
  allExecutions: (params: FetchAllExecutionsParams) =>
    ['erp', 'expenses', 'executions', 'all', params] as const,
};

const invalidateExpenseQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  keys: QueryKey[],
) => {
  keys.forEach((key) => {
    queryClient.invalidateQueries({ queryKey: key });
  });
};

export const extractExpenseError = (error: unknown): string => {
  if (isAxiosError<ApiErrorPayload>(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
    if (typeof error.response?.data?.message === 'string') {
      return error.response.data.message;
    }
    if (typeof error.message === 'string') {
      return error.message;
    }
  }
  return 'Ocurrió un error al procesar la operación de gastos.';
};

export interface FetchExpensesParams {
  includeInactive?: boolean;
  startDate?: string;
  endDate?: string;
  onlyPublicCostCenters?: boolean;
  costCenterCode?: string;
}

export interface FetchAllExecutionsParams {
  onlyExecuted?: boolean;
  startDate?: string;
  endDate?: string;
}

export const fetchExpenses = async (
  params: FetchExpensesParams = {},
): Promise<ExpenseOut[]> => {
  const queryParams: Record<string, any> = {};
  if (params.includeInactive) {
    queryParams.include_inactive = true;
  }
  if (params.startDate) {
    queryParams.start_date = params.startDate;
  }
  if (params.endDate) {
    queryParams.end_date = params.endDate;
  }
  if (params.onlyPublicCostCenters) {
    queryParams.only_public_cost_centers = true;
  }
  const { data } = await conns<ExpenseOut[]>(EXPENSES_URL_KEYS.list).get(
    Object.keys(queryParams).length > 0 ? queryParams : undefined
  );
  return data;
};

export const fetchExpenseDetail = async (
  expenseId: number,
): Promise<ExpenseDetailOut> => {
  const { data } = await conns<ExpenseDetailOut>(EXPENSES_URL_KEYS.detail, [expenseId]).get();
  return data;
};

export const fetchExpenseExecutions = async (
  expenseId: number,
): Promise<ExpenseExecutionOut[]> => {
  const { data } = await conns<ExpenseExecutionOut[]>(
    EXPENSES_URL_KEYS.executions,
    [expenseId, 'executions'],
  ).get();
  return data;
};

export const fetchAllExpenseExecutions = async (
  params: FetchAllExecutionsParams = {},
): Promise<ExpenseExecutionOut[]> => {
  const queryParams: Record<string, any> = {};
  if (params.onlyExecuted) {
    queryParams.only_executed = true;
  }
  if (params.startDate) {
    queryParams.start_date = params.startDate;
  }
  if (params.endDate) {
    queryParams.end_date = params.endDate;
  }
  const { data} = await conns<ExpenseExecutionOut[]>(
    EXPENSES_URL_KEYS.allExecutions,
    ['executions', 'all'],
  ).get(Object.keys(queryParams).length > 0 ? queryParams : undefined);
  return data;
};

export const createExpense = async (
  payload: ExpenseCreatePayload,
): Promise<ExpenseDetailOut> => {
  const { data } = await conns<ExpenseDetailOut>(EXPENSES_URL_KEYS.list).post(payload);
  return data;
};

export const updateExpense = async (
  expenseId: number,
  payload: ExpenseUpdatePayload,
): Promise<ExpenseDetailOut> => {
  const { data } = await conns<ExpenseDetailOut>(EXPENSES_URL_KEYS.detail, [expenseId]).patch(payload);
  return data;
};

export const archiveExpense = async (expenseId: number): Promise<void> => {
  await conns(EXPENSES_URL_KEYS.detail, [expenseId]).delete();
};

export const executeExpense = async (
  expenseId: number,
  payload: ExpenseExecutePayload,
): Promise<ExpenseDetailOut> => {
  const { data } = await conns<ExpenseDetailOut>(
    EXPENSES_URL_KEYS.execute,
    [expenseId, 'execute'],
  ).post(payload);
  return data;
};

export const scheduleExpense = async (
  expenseId: number,
  payload: ExpenseSchedulePayload,
): Promise<ExpenseDetailOut> => {
  const { data } = await conns<ExpenseDetailOut>(
    EXPENSES_URL_KEYS.schedule,
    [expenseId, 'schedule'],
  ).post(payload);
  return data;
};

export const rematerializeExpense = async (
  expenseId: number,
  payload?: ExpenseRematerializePayload,
): Promise<ExpenseDetailOut> => {
  const body = payload ?? {};
  const { data } = await conns<ExpenseDetailOut>(
    EXPENSES_URL_KEYS.rematerialize,
    [expenseId, 'rematerialize'],
  ).post(body);
  return data;
};

export const useExpensesQuery = <TData = ExpenseOut[]>(
  params: FetchExpensesParams,
  options?: Omit<
    UseQueryOptions<ExpenseOut[], AxiosError<ApiErrorPayload>, TData>,
    'queryKey' | 'queryFn'
  >,
): ExpensesQueryResult<TData> => {
  return useQuery<ExpenseOut[], AxiosError<ApiErrorPayload>, TData>({
    queryKey: EXPENSES_QUERY_KEYS.list(params),
    queryFn: () => fetchExpenses(params),
    staleTime: 60_000,
    ...options,
  });
};

export const useExpenseDetailQuery = <TData = ExpenseDetailOut>(
  expenseId: number | null,
  options?: Partial<
    Omit<UseQueryOptions<ExpenseDetailOut, AxiosError<ApiErrorPayload>, TData>, 'queryKey' | 'queryFn'>
  >,
): ExpenseDetailQueryResult<TData> => {
  return useQuery<ExpenseDetailOut, AxiosError<ApiErrorPayload>, TData>({
    queryKey: EXPENSES_QUERY_KEYS.detail(expenseId ?? 0),
    queryFn: () => fetchExpenseDetail(expenseId as number),
    enabled: typeof expenseId === 'number' && (options?.enabled ?? true),
    staleTime: 60_000,
    ...options,
  });
};

export const useExpenseExecutionsQuery = <TData = ExpenseExecutionOut[]>(
  expenseId: number | null,
  options?: Partial<
    Omit<UseQueryOptions<ExpenseExecutionOut[], AxiosError<ApiErrorPayload>, TData>, 'queryKey' | 'queryFn'>
  >,
): ExpenseExecutionsQueryResult<TData> => {
  return useQuery<ExpenseExecutionOut[], AxiosError<ApiErrorPayload>, TData>({
    queryKey: EXPENSES_QUERY_KEYS.executions(expenseId ?? 0),
    queryFn: () => fetchExpenseExecutions(expenseId as number),
    enabled: typeof expenseId === 'number' && (options?.enabled ?? true),
    staleTime: 60_000,
    ...options,
  });
};

export const useAllExpenseExecutionsQuery = <TData = ExpenseExecutionOut[]>(
  params: FetchAllExecutionsParams,
  options?: Omit<
    UseQueryOptions<ExpenseExecutionOut[], AxiosError<ApiErrorPayload>, TData>,
    'queryKey' | 'queryFn'
  >,
): ExpenseExecutionsQueryResult<TData> => {
  return useQuery<ExpenseExecutionOut[], AxiosError<ApiErrorPayload>, TData>({
    queryKey: EXPENSES_QUERY_KEYS.allExecutions(params),
    queryFn: () => fetchAllExpenseExecutions(params),
    staleTime: 60_000,
    ...options,
  });
};

export const useExpenseMutations = () => {
  const queryClient = useQueryClient();

  const createMutation = useMutation<
    ExpenseDetailOut,
    AxiosError<ApiErrorPayload>,
    ExpenseCreatePayload
  >({
    mutationFn: createExpense,
    onSuccess: () => {
      invalidateExpenseQueries(queryClient, [EXPENSES_QUERY_KEYS.all]);
    },
  });

  const updateMutation = useMutation<
    ExpenseDetailOut,
    AxiosError<ApiErrorPayload>,
    { expenseId: number; payload: ExpenseUpdatePayload }
  >({
    mutationFn: ({ expenseId, payload }) => updateExpense(expenseId, payload),
    onSuccess: (_, { expenseId }) => {
      invalidateExpenseQueries(queryClient, [
        EXPENSES_QUERY_KEYS.all,
        EXPENSES_QUERY_KEYS.detail(expenseId),
        EXPENSES_QUERY_KEYS.executions(expenseId),
      ]);
    },
  });

  const archiveMutation = useMutation<void, AxiosError<ApiErrorPayload>, number>({
    mutationFn: archiveExpense,
    onSuccess: (_, expenseId) => {
      invalidateExpenseQueries(queryClient, [
        EXPENSES_QUERY_KEYS.all,
        EXPENSES_QUERY_KEYS.detail(expenseId),
        EXPENSES_QUERY_KEYS.executions(expenseId),
      ]);
    },
  });

  const executeMutation = useMutation<
    ExpenseDetailOut,
    AxiosError<ApiErrorPayload>,
    { expenseId: number; payload: ExpenseExecutePayload }
  >({
    mutationFn: ({ expenseId, payload }) => executeExpense(expenseId, payload),
    onSuccess: (_, { expenseId }) => {
      invalidateExpenseQueries(queryClient, [
        EXPENSES_QUERY_KEYS.all,
        EXPENSES_QUERY_KEYS.detail(expenseId),
        EXPENSES_QUERY_KEYS.executions(expenseId),
      ]);
    },
  });

  const scheduleMutation = useMutation<
    ExpenseDetailOut,
    AxiosError<ApiErrorPayload>,
    { expenseId: number; payload: ExpenseSchedulePayload }
  >({
    mutationFn: ({ expenseId, payload }) => scheduleExpense(expenseId, payload),
    onSuccess: (_, { expenseId }) => {
      invalidateExpenseQueries(queryClient, [
        EXPENSES_QUERY_KEYS.all,
        EXPENSES_QUERY_KEYS.detail(expenseId),
        EXPENSES_QUERY_KEYS.executions(expenseId),
      ]);
    },
  });

  const rematerializeMutation = useMutation<
    ExpenseDetailOut,
    AxiosError<ApiErrorPayload>,
    { expenseId: number; payload?: ExpenseRematerializePayload }
  >({
    mutationFn: ({ expenseId, payload }) => rematerializeExpense(expenseId, payload),
    onSuccess: (_, { expenseId }) => {
      invalidateExpenseQueries(queryClient, [
        EXPENSES_QUERY_KEYS.all,
        EXPENSES_QUERY_KEYS.detail(expenseId),
        EXPENSES_QUERY_KEYS.executions(expenseId),
      ]);
    },
  });

  return {
    createMutation,
    updateMutation,
    archiveMutation,
    executeMutation,
    scheduleMutation,
    rematerializeMutation,
  };
};

export type {
  ExpenseDetailQueryResult,
  ExpenseExecutionsQueryResult,
  ExpensesQueryResult,
};
